import { useRef, useEffect, useState } from 'react';

const MAX_FEATURES = 15;

export default function SHAPInteractionHeatmap({ interactions }) {
  const canvasRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  if (!interactions?.length) {
    return <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>No SHAP interaction data available.</div>;
  }

  // Collect unique features (top MAX_FEATURES by max interaction value)
  const featureSet = new Set();
  for (const item of interactions) {
    featureSet.add(item.feature_a);
    featureSet.add(item.feature_b);
    if (featureSet.size >= MAX_FEATURES * 2) break;
  }
  // Rank features by sum of their interaction values
  const featureScores = {};
  for (const item of interactions) {
    featureScores[item.feature_a] = (featureScores[item.feature_a] || 0) + item.value;
    featureScores[item.feature_b] = (featureScores[item.feature_b] || 0) + item.value;
  }
  const features = Object.keys(featureScores)
    .sort((a, b) => featureScores[b] - featureScores[a])
    .slice(0, MAX_FEATURES);

  const n = features.length;
  const cellSize = 40;
  const labelWidth = 140;
  const labelHeight = 140;
  const canvasW = labelWidth + n * cellSize;
  const canvasH = labelHeight + n * cellSize;

  // Build value matrix
  const matrix = Array.from({ length: n }, () => Array(n).fill(0));
  const maxVal = interactions[0]?.value || 1;
  for (const item of interactions) {
    const i = features.indexOf(item.feature_a);
    const j = features.indexOf(item.feature_b);
    if (i >= 0 && j >= 0) {
      matrix[i][j] = item.value;
      matrix[j][i] = item.value;
    }
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvasW, canvasH);

    // Draw cells
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        const v = i === j ? 0 : matrix[i][j];
        const intensity = Math.min(1, v / maxVal);
        const r = Math.round(255 * (1 - intensity * 0.8));
        const g = Math.round(255 * (1 - intensity * 0.8));
        const b = 255;
        ctx.fillStyle = `rgb(${r},${g},${b})`;
        ctx.fillRect(labelWidth + j * cellSize, labelHeight + i * cellSize, cellSize - 1, cellSize - 1);
      }
    }

    // Draw row labels
    ctx.fillStyle = '#374151';
    ctx.font = '11px monospace';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let i = 0; i < n; i++) {
      const label = features[i].length > 18 ? features[i].slice(0, 17) + '…' : features[i];
      ctx.fillText(label, labelWidth - 4, labelHeight + i * cellSize + cellSize / 2);
    }

    // Draw col labels (rotated)
    ctx.save();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    for (let j = 0; j < n; j++) {
      const label = features[j].length > 18 ? features[j].slice(0, 17) + '…' : features[j];
      ctx.save();
      ctx.translate(labelWidth + j * cellSize + cellSize / 2, labelHeight - 4);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(label, 0, 0);
      ctx.restore();
    }
    ctx.restore();
  }, [interactions, n, canvasW, canvasH, maxVal]);

  function handleMouseMove(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const j = Math.floor((x - labelWidth) / cellSize);
    const i = Math.floor((y - labelHeight) / cellSize);
    if (i >= 0 && i < n && j >= 0 && j < n && i !== j) {
      setTooltip({ x: e.clientX, y: e.clientY, text: `${features[i]} × ${features[j]}: ${matrix[i][j].toFixed(4)}` });
    } else {
      setTooltip(null);
    }
  }

  return (
    <div style={{ position: 'relative', overflowX: 'auto' }}>
      <canvas ref={canvasRef} width={canvasW} height={canvasH}
        onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)}
        style={{ cursor: 'crosshair', display: 'block' }} />
      {tooltip && (
        <div style={{ position: 'fixed', left: tooltip.x + 12, top: tooltip.y - 8, background: 'rgba(17,24,39,0.9)', color: '#fff', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', pointerEvents: 'none', zIndex: 9999 }}>
          {tooltip.text}
        </div>
      )}
      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '6px' }}>
        Top {n} features by SHAP interaction strength. White = low, blue = high.
      </div>
    </div>
  );
}
